/**
 * Card — the single way to draw a white surface (design-system-lld.md §4.1).
 * Rewrites the old shadcn `card.tsx` (0 users in `features/`) in place.
 *
 * `features/` has zero callers of the old API, but ~30 legacy `pages/`
 * files (pre-overhaul CRM screens, still routed) import the shadcn
 * `Card/CardHeader/CardTitle/CardDescription/CardContent` composition —
 * those must keep compiling. Compat shape:
 *   - `CardHeader` accepts the new `title/description/actions` props OR
 *     legacy `children` (e.g. `<CardHeader><CardTitle/></CardHeader>`) —
 *     it always renders with the canonical header chrome
 *     (px-6 py-4 border-b border-gray-100).
 *   - `CardTitle` / `CardDescription` are kept as legacy exports, restyled
 *     to the token type scale.
 *   - `CardContent` is a legacy alias of the new `CardBody`.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

export type CardPadding = 'default' | 'dense' | 'none';

const PADDING_CLASSES: Record<CardPadding, string> = {
  default: 'p-6',
  dense: 'p-4',
  none: '',
};

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding;
  as?: 'div' | 'section' | 'form';
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding = 'default', as = 'div', ...props }, ref) => {
    const Comp = as as React.ElementType;
    return (
      <Comp
        ref={ref}
        className={cn(
          'rounded-xl border border-gray-200 bg-white',
          PADDING_CLASSES[padding],
          className,
        )}
        {...props}
      />
    );
  },
);
Card.displayName = 'Card';

export interface CardHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, actions, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4', className)}
      {...props}
    >
      {title !== undefined ? (
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
        </div>
      ) : (
        <div className="min-w-0 flex-1">{children}</div>
      )}
      {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
    </div>
  ),
);
CardHeader.displayName = 'CardHeader';

/** @deprecated legacy shadcn export — use the `title` prop on `CardHeader`. */
const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-base font-semibold text-gray-900', className)} {...props} />
  ),
);
CardTitle.displayName = 'CardTitle';

/** @deprecated legacy shadcn export — use the `description` prop on `CardHeader`. */
const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('mt-0.5 text-sm text-gray-500', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

const CardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('p-6', className)} {...props} />,
);
CardBody.displayName = 'CardBody';

/** @deprecated legacy shadcn export — alias of `CardBody`. */
const CardContent = CardBody;

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-2 border-t border-gray-100 px-6 py-4', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardBody, CardFooter, CardTitle, CardDescription, CardContent };
